import { useCallback, useEffect, useState } from 'react';
import api from '../../services/api';
import type { NewsBeatSignal, NewsBeatVerdict } from '../../types';

/**
 * What the last run says about these settings.
 *
 * Computed server-side (jcseo/news/verdict.py) and rendered here verbatim, so
 * this strip and the tuning agent are reading one source. If the agent were
 * left to derive "41 of 44 were dropped by state" itself it would eventually
 * disagree with what's on screen, and then the user believes neither.
 *
 * Silence is a real state: a run with nothing wrong renders one quiet line. A
 * panel that always finds something to say is a panel people stop reading.
 */

const TONE: Record<NewsBeatSignal['severity'], string> = {
  blocker:
    'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300',
  warn: 'border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300',
  note: 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300',
};

export default function BeatVerdictStrip({
  beatId,
  reloadKey,
  onAsk,
}: {
  beatId: number;
  /** Bump to re-read after a run or a settings change. */
  reloadKey?: number;
  onAsk: (question: string) => void;
}) {
  const [verdict, setVerdict] = useState<NewsBeatVerdict | null>(null);

  const load = useCallback(async () => {
    try {
      setVerdict(await api.getNewsBeatVerdict(beatId));
    } catch {
      // Informational. A failed lookup hides the strip rather than breaking
      // the Setup form it sits above.
      setVerdict(null);
    }
  }, [beatId]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  if (!verdict) return null;

  const { counts, signals } = verdict;
  const ran = verdict.briefing_id !== null;

  return (
    <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          What the last run says
        </h3>
        {ran && (
          <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            {counts.fetched} fetched · {counts.stories} stories ·{' '}
            {counts.filtered_out} filtered out
            {counts.muted_stories > 0 && ` · ${counts.muted_stories} muted`}
          </span>
        )}
      </div>

      {signals.length === 0 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Nothing looks wrong with the settings from the counts alone.
        </p>
      ) : (
        <ul className="space-y-2">
          {signals.map((s) => (
            <li
              key={s.key}
              className={`rounded-md border px-2.5 py-2 text-xs ${TONE[s.severity]}`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="font-medium">{s.label}</span>
                <button
                  type="button"
                  onClick={() =>
                    onAsk(`About "${s.label}" on the last run - ${s.detail} What do you suggest?`)
                  }
                  className="underline underline-offset-2 opacity-80 hover:opacity-100"
                >
                  Ask the tuner
                </button>
              </div>
              <p className="mt-0.5 leading-snug opacity-90">{s.detail}</p>
              <p className="mt-0.5 leading-snug opacity-75">{s.fix}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
